import { defaultRuntimePricingCatalog, formatCurrencyAmount } from "@/features/pricing/runtimePricing";
import type {
  BillingCheckoutRequest,
  BillingOffer,
  BillingOfferCode,
  BillingOverview,
  BillingPageState,
} from "@/features/billing/billing.types";

export function getBillingOffer(
  offerCode: BillingOfferCode | null | undefined,
  offers: BillingOffer[] = defaultRuntimePricingCatalog.billingOffers,
) {
  return offers.find((offer) => offer.offerCode === offerCode) ?? null;
}

export function getSubscriptionOffers(
  offers: BillingOffer[] = defaultRuntimePricingCatalog.billingOffers,
) {
  return offers.filter((offer): offer is Extract<BillingOffer, { kind: "subscription" }> =>
    offer.kind === "subscription"
  );
}

export function getTopUpOffers(
  offers: BillingOffer[] = defaultRuntimePricingCatalog.billingOffers,
) {
  return offers.filter((offer): offer is Extract<BillingOffer, { kind: "top_up" }> =>
    offer.kind === "top_up"
  );
}

export function formatBillingOfferAmount(offer: BillingOffer, locale = "en") {
  return formatCurrencyAmount(offer.priceCents, offer.currencyCode, locale);
}

export function formatBillingOfferPrice(
  offer: BillingOffer,
  input: {
    intervalLabel?: string | null;
    locale?: string;
  } = {},
) {
  const amount = formatBillingOfferAmount(offer, input.locale);

  if (offer.kind !== "subscription" || !input.intervalLabel) {
    return amount;
  }

  return `${amount} / ${input.intervalLabel}`;
}

export function getCurrentPlanOfferCode(
  overview: BillingOverview,
  offers: BillingOffer[] = defaultRuntimePricingCatalog.billingOffers,
) {
  if (overview.subscription.tier === "free" || !overview.subscription.billingInterval) {
    return null;
  }

  const matchingOffer = offers.find((offer) =>
    offer.kind === "subscription"
    && offer.tier === overview.subscription.tier
    && offer.billingInterval === overview.subscription.billingInterval
  );

  return matchingOffer?.offerCode ?? null;
}

export function buildBillingPageState(
  overview: BillingOverview,
  offers: BillingOffer[] = defaultRuntimePricingCatalog.billingOffers,
): BillingPageState {
  const subscriptionOffers = getSubscriptionOffers(offers);
  const topUpOffers = getTopUpOffers(offers);
  const currentPlanOfferCode = getCurrentPlanOfferCode(overview, offers);

  return {
    canManageBilling: overview.subscription.tier !== "free",
    canTopUp: overview.subscription.tier !== "free",
    checkoutTierOfferCodes: subscriptionOffers.map((offer) => offer.offerCode),
    currentPlanOfferCode,
    currentTier: overview.subscription.tier,
    history: overview.history,
    managementStatus:
      overview.subscription.tier === "free"
        ? "free"
        : overview.subscription.cancelAtPeriodEnd
          ? "cancel_scheduled"
          : "active",
    nextBillingDate: overview.subscription.currentPeriodEnd,
    nextResetAt: overview.wallet.allocationResetAt,
    offers,
    topUpOfferCodes:
      overview.subscription.tier === "free" ? [] : topUpOffers.map((offer) => offer.offerCode),
    wallet: overview.wallet,
  };
}

export function buildCheckoutRequest(
  offerCode: BillingOfferCode,
  offers: BillingOffer[] = defaultRuntimePricingCatalog.billingOffers,
): BillingCheckoutRequest {
  const offer = getBillingOffer(offerCode, offers);

  if (!offer) {
    throw new Error(`Unknown billing offer: ${offerCode}`);
  }

  return {
    mode: offer.kind === "top_up" ? "payment" : "subscription",
    offerCode,
  };
}
